import type { ImageConfig } from '@uefadigital/panenka';
import { getWidthSrcSet, ImageConfigType } from '@uefadigital/panenka';
import lazySizes from 'lazysizes';
import 'lazysizes/plugins/attrchange/ls.attrchange';
import 'lazysizes/plugins/bgset/ls.bgset';

import EventBusInstance from '@core/eventbus';
import { IS_BAD_NETWORK_DETECTED } from '@utilities/browser';
import { MemoryCache } from '@utilities/memory-cache';

export type { ImageConfig };
export { ImageConfigType };

window.imageSizesConfig = window.imageSizesConfig?.map((c) => ({
  ...c,
  match: c.match && new RegExp(c.match),
  path: c.path && new RegExp(c.path),
}));

export default class LazyloadService {
  static readonly nativeLoadingSupport = 'loading' in HTMLImageElement.prototype;

  static loadFallback = (ev: Event) => {
    const img = ev.target as HTMLImageElement,
      fallbackSrc = img.dataset.fallback,
      currentSrc = img.currentSrc ?? img.srcset ?? img.src ?? '';

    const imgExists = ev.type !== 'error';

    if (imgExists) {
      if (fallbackSrc) {
        img.classList.remove('lazyfallback');
      }

      return;
    }

    if (!fallbackSrc) {
      img.classList.add('lazyfallback');
      return;
    }

    const setFallback = (element: HTMLImageElement | HTMLSourceElement) => {
      const dataSrcset = element.dataset.srcset || element.srcset || element.dataset.src || element.src || '';
      if (dataSrcset.includes(fallbackSrc) || !dataSrcset.includes(currentSrc)) return;

      if (element.dataset.srcset) element.dataset.srcset = fallbackSrc;
      if (element.dataset.src) element.dataset.src = fallbackSrc;
    };

    if (img.parentElement?.matches('picture')) {
      const sourceElements = img.parentElement.querySelectorAll('source');
      Array.from(sourceElements).forEach((source) => setFallback(source));
    }

    setFallback(img);
  };

  static cache = new MemoryCache<string, boolean>();

  static checkImageExists = (src: string): Promise<boolean> =>
    new Promise((resolve) => {
      if (!src) return false;

      if (LazyloadService.cache.has(src)) {
        resolve(LazyloadService.cache.get(src));
        return;
      }

      const img = new Image();

      img.onerror = () => {
        LazyloadService.cache.set(src, false);
        resolve(false);
      };
      img.onload = () => {
        LazyloadService.cache.set(src, true);
        resolve(true);
      };

      img.src = src;
    });

  static lazyCallback = async (ev: Event) => {
    const el = ev.target as HTMLImageElement;
    el.classList.remove('pflazy-bg');

    el.addEventListener('load', LazyloadService.loadFallback);
    el.addEventListener('error', LazyloadService.loadFallback);

    if (el.dataset.bgset && el.dataset.fallback && !el.dataset.bgset.includes(el.dataset.fallback)) {
      const src = getWidthSrcSet(el.dataset.bgset, true);
      const exists = await LazyloadService.checkImageExists(src);
      if (!exists) {
        el.dataset.bgset = el.dataset.fallback;
      }
    }

    if (el.dataset.srcset && !/ \d+w,?/.test(el.dataset.srcset)) {
      const srcset = getWidthSrcSet(el.dataset.srcset);
      if (srcset !== el.dataset.srcset) el.dataset.srcset = srcset;
    }

    if (el.parentElement?.matches('picture')) {
      Array.from(el.parentElement.querySelectorAll('source')).forEach((source) => {
        if (source.dataset.srcset && !/ \d+w,?/.test(source.dataset.srcset)) {
          const srcset = getWidthSrcSet(source.dataset.srcset);
          if (srcset !== source.dataset.srcset) source.dataset.srcset = srcset;
        }
      });
    }
  };
}

lazySizes.cfg.lazyClass = 'pflazyload';
lazySizes.cfg.minSize = 20;
lazySizes.cfg.loadHidden = false;
lazySizes.cfg.loadMode = IS_BAD_NETWORK_DETECTED ? 1 : 2;

document.addEventListener('lazybeforeunveil', LazyloadService.lazyCallback);

EventBusInstance.on('requestLazyRecalc', () => {
  lazySizes.autoSizer.checkElems();
});
